import { collection, getDocs, doc, getDoc, query, where } from "firebase/firestore";
import { firestore } from "../firebase";
import { ChartData, TrackData } from "../types/Types";

export const ChartService = {

  fetchTrendingCharts: async (): Promise<ChartData[]> => {
    try {
      const chartsRef = collection(firestore, "stateCharts");
      const snapshot = await getDocs(chartsRef);

      const allCharts = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          stateName: data.stateName,
          stateNameAlias: data.stateNameAlias,
          stateImage: data.stateImage,
          chartTrackIDs: data.chartTrackIDs || [],
        } as ChartData;
      });

      const sortedCharts = allCharts
        .filter((chart) => chart.chartTrackIDs.length > 0)
        .sort((a, b) => b.chartTrackIDs.length - a.chartTrackIDs.length);

      const top10Charts = sortedCharts.slice(0, 10);

      const chartsWithCoversPromises = top10Charts.map(async (chart) => {
        const firstTrackID = chart.chartTrackIDs[0];
        if (!firstTrackID) return chart;

        try {
          const trackRef = doc(firestore, "trackLinks", firstTrackID);
          const trackSnap = await getDoc(trackRef);

          if (trackSnap.exists()) {
            const trackData = trackSnap.data();
            return {
              ...chart,
              coverArtUrl: trackData.coverArtUrl || "",
            };
          }
        } catch (error) {
          console.error(`Error fetching cover for chart ${chart.stateName}:`, error);
        }

        return chart;
      });

      return await Promise.all(chartsWithCoversPromises);

    } catch (error) {
      console.error("Error fetching trending charts:", error);
      return [];
    }
  },

  fetchChartDetailsByAlias: async (alias: string): Promise<ChartData | null> => {
    try {
      const chartsRef = collection(firestore, "stateCharts");
      const q = query(chartsRef, where("stateNameAlias", "==", alias));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        console.warn(`No chart found for alias: ${alias}`);
        return null;
      }

      const docSnapshot = querySnapshot.docs[0];
      const data = docSnapshot.data();

      const chartData: ChartData = {
        id: docSnapshot.id,
        stateName: data.stateName,
        stateNameAlias: data.stateNameAlias,
        stateImage: data.stateImage || "",
        chartTrackIDs: data.chartTrackIDs || [],
        tracks: []
      };

      if (chartData.chartTrackIDs.length > 0) {
        const trackPromises = chartData.chartTrackIDs.map(async (trackId, index) => {
          try {
            const trackRef = doc(firestore, "trackLinks", trackId);
            const trackSnap = await getDoc(trackRef);

            if (trackSnap.exists()) {
              const tData = trackSnap.data();

              let finalUserName = tData.userName || "";

              if (!finalUserName && tData.userId) {
                try {
                  const userRef = doc(firestore, "users", tData.userId);
                  const userSnap = await getDoc(userRef);
                  if (userSnap.exists()) {
                    finalUserName = userSnap.data().userName || "";
                  }
                } catch (err) {
                  console.error(`Error fetching user profile for ${tData.userId}`, err);
                }
              }

              return {
                id: trackSnap.id,
                rank: index + 1,
                userId: tData.userId || "",
                stateID: tData.stateID || 0,
                regionID: tData.regionID || 0,
                coverArtUrl: tData.coverArtUrl || "",
                userName: finalUserName,
                trackTitle: tData.trackTitle || "Unknown Title",
                artistName: tData.artistName || "Unknown Artist",
                featureArtists: tData.featureArtists || [],
                genreName: tData.genre || "",
                releaseType: tData.releaseType || "",
                platformLinks: tData.platformLinks || {},
                moodTags: tData.moodTags || []
              } as TrackData;
            }
          } catch (err) {
            console.error(`Error fetching track ${trackId}`, err);
          }
          return null;
        });

        const fetchedTracks = await Promise.all(trackPromises);
        chartData.tracks = fetchedTracks.filter((t): t is TrackData => t !== null);
      }

      return chartData;

    } catch (error) {
      console.error("Error fetching chart details:", error);
      return null;
    }
  }
};